import { Injectable, signal, computed, OnDestroy } from '@angular/core';

export type Breakpoint = 'mobile' | 'tablet' | 'desktop';

/**
 * Responsive service for detecting viewport breakpoints.
 * Uses Angular signals for reactive updates.
 * 
 * Breakpoints:
 * - mobile: < 640px
 * - tablet: 640px - 1023px
 * - desktop: >= 1024px
 */
@Injectable({
  providedIn: 'root'
})
export class ResponsiveService implements OnDestroy {
  private readonly MOBILE_MAX = 639;
  private readonly TABLET_MAX = 1023;
  
  private readonly _breakpoint = signal<Breakpoint>(this.detectBreakpoint());
  private readonly _width = signal<number>(typeof window !== 'undefined' ? window.innerWidth : 1024);
  
  private resizeListener: (() => void) | null = null;
  
  /** Current breakpoint */
  readonly breakpoint = this._breakpoint.asReadonly();
  
  /** Current viewport width */
  readonly width = this._width.asReadonly();
  
  /** Is mobile viewport (< 640px) */
  readonly isMobile = computed(() => this._breakpoint() === 'mobile');
  
  /** Is tablet viewport (640px - 1023px) */
  readonly isTablet = computed(() => this._breakpoint() === 'tablet');
  
  /** Is desktop viewport (>= 1024px) */
  readonly isDesktop = computed(() => this._breakpoint() === 'desktop');
  
  /** Is mobile or tablet (< 1024px) */
  readonly isMobileOrTablet = computed(() => 
    this._breakpoint() === 'mobile' || this._breakpoint() === 'tablet'
  );
  
  /** Is tablet or desktop (>= 640px) */
  readonly isTabletOrDesktop = computed(() => 
    this._breakpoint() === 'tablet' || this._breakpoint() === 'desktop'
  );
  
  constructor() {
    if (typeof window !== 'undefined') {
      this.resizeListener = () => this.onResize();
      window.addEventListener('resize', this.resizeListener);
    }
  }
  
  ngOnDestroy() {
    if (this.resizeListener && typeof window !== 'undefined') {
      window.removeEventListener('resize', this.resizeListener);
    }
  }
  
  private onResize() {
    const width = window.innerWidth;
    this._width.set(width);
    this._breakpoint.set(this.detectBreakpoint());
  }
  
  private detectBreakpoint(): Breakpoint {
    if (typeof window === 'undefined') return 'desktop';
    
    const width = window.innerWidth;
    if (width <= this.MOBILE_MAX) return 'mobile';
    if (width <= this.TABLET_MAX) return 'tablet';
    return 'desktop';
  }
  
  /**
   * Check if viewport matches a specific breakpoint
   */
  matches(breakpoint: Breakpoint): boolean {
    return this._breakpoint() === breakpoint;
  }
  
  /**
   * Check if viewport is at least the specified breakpoint
   * (mobile < tablet < desktop)
   */
  isAtLeast(breakpoint: Breakpoint): boolean {
    const order: Breakpoint[] = ['mobile', 'tablet', 'desktop'];
    const currentIndex = order.indexOf(this._breakpoint());
    const targetIndex = order.indexOf(breakpoint);
    return currentIndex >= targetIndex;
  }
  
  /**
   * Check if viewport is at most the specified breakpoint
   */
  isAtMost(breakpoint: Breakpoint): boolean {
    const order: Breakpoint[] = ['mobile', 'tablet', 'desktop'];
    const currentIndex = order.indexOf(this._breakpoint());
    const targetIndex = order.indexOf(breakpoint);
    return currentIndex <= targetIndex;
  }
}
